import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { getErrorMessage } from "@/lib/errorUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Copy, 
  Check, 
  LogOut, 
  Link as LinkIcon, 
  Building2, 
  CreditCard,
  Send,
  Loader2,
  ExternalLink,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  ClipboardList
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("referral");
  
  const [billingInfo, setBillingInfo] = useState({
    company_name: "",
    tax_code: "",
    address: "",
    sdi_pec: "",
    iban: ""
  });
  
  const [referralForm, setReferralForm] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    team_size: "",
    notes: ""
  });
  
  const [balance, setBalance] = useState({ balance: 0, pending_payout: 0 });
  const [transactions, setTransactions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");

  const referralLink = user ? `https://aiclo.io/aiclo-ref/?ref=${user.referral_code}` : "";

  useEffect(() => {
    if (user) {
      fetchBillingInfo();
      fetchBalance();
      fetchTransactions();
      fetchReferrals();
    }
  }, [user]);

  const fetchBillingInfo = async () => {
    try {
      const response = await axios.get(`${API}/billing-info`, { headers: getAuthHeader() });
      setBillingInfo(response.data);
    } catch (error) {
      console.error("Error fetching billing info:", error);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/balance`, { headers: getAuthHeader() });
      setBalance(response.data);
    } catch (error) {
      console.error("Error fetching balance:", error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions`, { headers: getAuthHeader() });
      setTransactions(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/referrals`, { headers: getAuthHeader() });
      setReferrals(response.data);
    } catch (error) {
      console.error("Error fetching referrals:", error);
    }
  };

  const maskName = (name) => {
    if (!name || name.length <= 2) return name;
    return name.slice(0, 2) + "*".repeat(name.length - 2);
  };

  const statusConfig = {
    pending: { label: "In gestione", color: "bg-blue-500/20 text-blue-400", tooltip: "Il lead è in lavorazione dai nostri consulenti" },
    in_gestione: { label: "In gestione", color: "bg-blue-500/20 text-blue-400", tooltip: "Il lead è in lavorazione dai nostri consulenti" },
    sottoscrizione_avvenuta: { label: "Sottoscrizione avvenuta", color: "bg-green-500/20 text-green-400", tooltip: "Il cliente ha sottoscritto Aiclo, la tua commissione sarà accreditata una volta usciti dalla finestra di possibile recesso, di solito dopo 60 giorni" },
    vendita_confermata: { label: "Vendita confermata", color: "bg-emerald-500/20 text-emerald-300", tooltip: "Il cliente ha sottoscritto Aiclo ed è uscito dalla finestra di possibile recesso. La tua commissione sarà accreditata a breve sul tuo account" },
    perso: { label: "Perso", color: "bg-red-500/20 text-red-400", tooltip: "La trattativa ha avuto esito negativo. Puoi ricontattare il cliente per maggiori informazioni." },
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Link copiato negli appunti!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Errore nel copiare il link");
    }
  };

  const handleSaveBilling = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.put(`${API}/billing-info`, billingInfo, { headers: getAuthHeader() });
      toast.success("Dati di fatturazione salvati!");
    } catch (error) {
      toast.error("Errore nel salvataggio dei dati");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitReferral = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = {
        ...referralForm,
        team_size: referralForm.team_size || null
      };
      await axios.post(`${API}/referrals`, data, { headers: getAuthHeader() });
      toast.success("Azienda segnalata con successo!");
      setReferralForm({
        company_name: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        team_size: "",
        notes: ""
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nella segnalazione"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/payout-request`, {
        amount: parseFloat(payoutAmount),
        notes: payoutNotes
      }, { headers: getAuthHeader() });
      toast.success("Richiesta payout inviata!");
      setPayoutAmount("");
      setPayoutNotes("");
      fetchBalance();
      fetchTransactions();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nella richiesta"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logout effettuato");
  };

  const availableBalance = balance.balance - balance.pending_payout;

  return (
    <div className="min-h-screen bg-background" data-testid="user-dashboard">
      {/* Background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[600px] h-[400px] bg-[#fcb900]/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-[#fc0301]/5 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="border-b border-white/5 bg-black/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://aiclo.io/wp-content/uploads/2025/09/aiclo.svg" 
              alt="Aiclo" 
              className="h-6"
            />
            <div>
              <p className="text-sm text-muted-foreground">
                Ciao, {user?.first_name} {user?.last_name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-foreground"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Esci
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl relative z-10">
        {/* Referral Link Card - Prominent */}
        <motion.div
          className="glass-card p-8 mb-8 relative overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#fcb900]/8 rounded-full blur-3xl -mr-32 -mt-32" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#fcb900]/20 flex items-center justify-center">
                <LinkIcon className="w-5 h-5 text-[#fcb900]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Il tuo link di segnalazione</h2>
                <p className="text-sm text-muted-foreground">
                  Condividi questo link con imprenditori e manager
                </p>
              </div>
            </div>
            
            <p className="text-muted-foreground mb-6">
              Quando un'azienda si registra tramite il tuo link e diventa cliente, riceverai il 20% del valore del primo anno di abbonamento.
            </p>
            
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Input
                  value={referralLink}
                  readOnly
                  className="bg-black/40 border-[#fcb900]/30 h-12 pr-12 font-mono text-sm"
                  data-testid="referral-link-input"
                />
                <a 
                  href={referralLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#fcb900]"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <Button
                onClick={copyToClipboard}
                className={`h-12 px-6 rounded-xl btn-gold transition-all`}
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copiato!" : "Copia"}
              </Button>
            </div>
            
            <div className="mt-4 p-3 rounded-lg bg-[#fcb900]/5 border border-[#fcb900]/20">
              <p className="text-sm">
                <span className="text-muted-foreground">Il tuo codice referral:</span>{" "}
                <span className="font-mono font-semibold text-[#fcb900]" data-testid="referral-code">{user?.referral_code}</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 bg-black/50 border border-white/10">
            <TabsTrigger value="referral" className="data-[state=active]:bg-[#fcb900]/20 data-[state=active]:text-white" data-testid="tab-referral">
              <Building2 className="w-4 h-4 mr-2" />
              Segnala Azienda
            </TabsTrigger>
            <TabsTrigger value="segnalazioni" className="data-[state=active]:bg-[#fcb900]/20 data-[state=active]:text-white" data-testid="tab-segnalazioni">
              <ClipboardList className="w-4 h-4 mr-2" />
              Segnalazioni ({referrals.length})
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-[#fcb900]/20 data-[state=active]:text-white" data-testid="tab-billing">
              <CreditCard className="w-4 h-4 mr-2" />
              Fatturazione
            </TabsTrigger>
            <TabsTrigger value="wallet" className="data-[state=active]:bg-[#fcb900]/20 data-[state=active]:text-white" data-testid="tab-wallet">
              <Wallet className="w-4 h-4 mr-2" />
              Saldo e Movimenti
            </TabsTrigger>
          </TabsList>

          {/* Referral Form Tab */}
          <TabsContent value="referral">
            <motion.div
              className="glass-card p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h3 className="text-lg font-semibold mb-2">Segnala direttamente un'azienda</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Parla prima di Aiclo con il referente e avvertilo che sarà contattato per una demo gratuita e senza impegno.
              </p>
              
              <form onSubmit={handleSubmitReferral} className="space-y-4" data-testid="referral-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Azienda *</Label>
                    <Input
                      value={referralForm.company_name}
                      onChange={(e) => setReferralForm({ ...referralForm, company_name: e.target.value })}
                      className="bg-black/20 border-white/10 h-11"
                      required
                      data-testid="referral-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome Referente *</Label>
                    <Input
                      value={referralForm.contact_name}
                      onChange={(e) => setReferralForm({ ...referralForm, contact_name: e.target.value })}
                      className="bg-black/20 border-white/10 h-11"
                      required
                      data-testid="referral-contact-name"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email Referente *</Label>
                    <Input
                      type="email"
                      value={referralForm.contact_email}
                      onChange={(e) => setReferralForm({ ...referralForm, contact_email: e.target.value })}
                      className="bg-black/20 border-white/10 h-11"
                      required
                      data-testid="referral-contact-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono Referente *</Label>
                    <Input
                      type="tel"
                      value={referralForm.contact_phone}
                      onChange={(e) => setReferralForm({ ...referralForm, contact_phone: e.target.value })}
                      className="bg-black/20 border-white/10 h-11"
                      required
                      data-testid="referral-contact-phone"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Numero venditori nel team (opzionale)</Label>
                  <Select
                    value={referralForm.team_size}
                    onValueChange={(value) => setReferralForm({ ...referralForm, team_size: value })}
                  >
                    <SelectTrigger className="bg-black/20 border-white/10 h-11" data-testid="referral-team-size">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="1-3">1-3</SelectItem>
                      <SelectItem value="4-10">4-10</SelectItem>
                      <SelectItem value="11-50">11-50</SelectItem>
                      <SelectItem value="51-100">51-100</SelectItem>
                      <SelectItem value="100+">100+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Note (opzionale)</Label>
                  <Textarea
                    value={referralForm.notes}
                    onChange={(e) => setReferralForm({ ...referralForm, notes: e.target.value })}
                    className="bg-black/20 border-white/10 min-h-[100px]"
                    placeholder="Informazioni aggiuntive sull'azienda..."
                    data-testid="referral-notes"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-11 rounded-full btn-gold"
                  disabled={isLoading}
                  data-testid="submit-referral-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Invia Segnalazione
                </Button>
              </form>
            </motion.div>
          </TabsContent>

          {/* Segnalazioni Tab */}
          <TabsContent value="segnalazioni">
            <motion.div
              className="glass-card p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h3 className="text-lg font-semibold mb-4">Le tue segnalazioni</h3>
              {referrals.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Nessuna segnalazione ancora. Condividi il tuo link referral o compila il form per iniziare!
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="referrals-table">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-sm text-muted-foreground">
                        <th className="pb-3 font-medium">Azienda</th>
                        <th className="pb-3 font-medium">Referente</th>
                        <th className="pb-3 font-medium">Team Size</th>
                        <th className="pb-3 font-medium">Data</th>
                        <th className="pb-3 font-medium">Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((r) => {
                        const cfg = statusConfig[r.status] || statusConfig.pending;
                        return (
                          <tr key={r.id} className="border-b border-white/5 text-sm">
                            <td className="py-3">{maskName(r.company_name)}</td>
                            <td className="py-3 text-muted-foreground">{maskName(r.contact_name)}</td>
                            <td className="py-3 text-muted-foreground">{r.team_size || "-"}</td>
                            <td className="py-3 text-muted-foreground">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                            <td className="py-3">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-help ${cfg.color}`}
                                title={cfg.tooltip}
                              >
                                {cfg.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <motion.div
              className="glass-card p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h3 className="text-lg font-semibold mb-2">Registra i tuoi dati di fatturazione e pagamento</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Puoi farlo in qualsiasi momento
              </p>
              
              <form onSubmit={handleSaveBilling} className="space-y-4" data-testid="billing-form">
                <div className="space-y-2">
                  <Label>Ragione Sociale / Nome Cognome</Label>
                  <Input
                    value={billingInfo.company_name}
                    onChange={(e) => setBillingInfo({ ...billingInfo, company_name: e.target.value })}
                    className="bg-black/20 border-white/10 h-11"
                    data-testid="billing-company-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Codice Fiscale / P.IVA</Label>
                  <Input
                    value={billingInfo.tax_code}
                    onChange={(e) => setBillingInfo({ ...billingInfo, tax_code: e.target.value })}
                    className="bg-black/20 border-white/10 h-11"
                    data-testid="billing-tax-code"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Indirizzo completo</Label>
                  <Input
                    value={billingInfo.address}
                    onChange={(e) => setBillingInfo({ ...billingInfo, address: e.target.value })}
                    className="bg-black/20 border-white/10 h-11"
                    data-testid="billing-address"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Codice Destinatario / PEC</Label>
                  <Input
                    value={billingInfo.sdi_pec}
                    onChange={(e) => setBillingInfo({ ...billingInfo, sdi_pec: e.target.value })}
                    className="bg-black/20 border-white/10 h-11"
                    data-testid="billing-sdi-pec"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>IBAN per il pagamento</Label>
                  <Input
                    value={billingInfo.iban}
                    onChange={(e) => setBillingInfo({ ...billingInfo, iban: e.target.value })}
                    className="bg-black/20 border-white/10 h-11"
                    placeholder="IT..."
                    data-testid="billing-iban"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-11 rounded-full btn-gold"
                  disabled={isLoading}
                  data-testid="save-billing-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Salva
                </Button>
              </form>
            </motion.div>
          </TabsContent>

          {/* Wallet Tab */}
          <TabsContent value="wallet">
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {/* Balance Card */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="glass-card p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#fcb900]/20 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-[#fcb900]" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Saldo disponibile</p>
                      <p className="text-2xl font-bold text-gold-shimmer" data-testid="available-balance">
                        €{availableBalance.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  {balance.pending_payout > 0 && (
                    <p className="text-sm text-[#fcb900] flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      €{balance.pending_payout.toFixed(2)} in attesa di payout
                    </p>
                  )}
                </div>
                
                <div className="glass-card p-6">
                  <h4 className="font-medium mb-3">Richiedi Payout</h4>
                  <form onSubmit={handleRequestPayout} className="space-y-3">
                    <div>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Importo €"
                        value={payoutAmount}
                        onChange={(e) => setPayoutAmount(e.target.value)}
                        className="bg-black/20 border-white/10 h-10"
                        data-testid="payout-amount"
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Note (opzionale)"
                        value={payoutNotes}
                        onChange={(e) => setPayoutNotes(e.target.value)}
                        className="bg-black/20 border-white/10 h-10"
                        data-testid="payout-notes"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-10 rounded-lg btn-gold"
                      disabled={isLoading || availableBalance <= 0}
                      data-testid="request-payout-btn"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Richiedi Payout
                    </Button>
                  </form>
                </div>
              </div>

              {/* Transactions List */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4">Lista Movimenti</h3>
                
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground" data-testid="no-transactions">
                    <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun movimento presente.</p>
                    <p className="text-sm mt-2">I tuoi guadagni appariranno qui.</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="transactions-list">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-white/[0.02] border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            transaction.type === 'credit' 
                              ? 'bg-green-500/20' 
                              : transaction.type === 'payout_request' && transaction.status === 'pending'
                              ? 'bg-yellow-500/20'
                              : 'bg-red-500/20'
                          }`}>
                            {transaction.type === 'credit' ? (
                              <ArrowDownRight className="w-5 h-5 text-green-500" />
                            ) : transaction.type === 'payout_request' && transaction.status === 'pending' ? (
                              <Clock className="w-5 h-5 text-yellow-500" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5 text-red-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{transaction.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(transaction.created_at).toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${
                            transaction.type === 'credit' ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {transaction.type === 'credit' ? '+' : '-'}€{transaction.amount.toFixed(2)}
                          </p>
                          {transaction.status === 'pending' && (
                            <span className="text-xs text-yellow-500">In attesa</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
